import { FilePurpose } from '@/api/enums';
import { equal, handleAxiosError, nested } from '@/api/helpers';
import { ListingRequest } from '@/api/types';
import { client } from '@/client';
import {
  Account,
  AccountCourseDelivery,
  AccountFormState,
} from '@/client/accounts';
import { File } from '@/client/files';
import { MailSystemType, SendingProfileType } from '@/client/sender-profiles';
import { SystemRoles } from '@/client/users';
import { LoadingStateType } from '@/common/constants';
import {
  availableLanguagesDropdown,
  courseOnlyLanguages,
} from '@/common/constants/languages';
import {
  ACCOUNT_FIELD_EDITOR_ROLES,
  SUBDOMAIN_EDITOR_ROLES,
} from '@/common/constants/roles';
import { CompanyDescriptionSection } from '@/components/accounts/CompanyDescriptionSection';
import { SubdomainAssignmentConfirmDialog } from '@/components/accounts/SubdomainAssignmentConfirmDialog';
import { accountSchema } from '@/components/accounts/forms/validations/accounts';
import { OsintTransparencySection } from '@/components/accounts/osint/OsintTransparencySection';
import {
  FormikCheckbox,
  FormikDropdown,
  FormikIconInput,
  FormikTextarea,
} from '@/components/form';
import { FormikChips } from '@/components/form/FormikChips';
import { FormikInput } from '@/components/form/FormikInput';
import { FormikLazyDropdown } from '@/components/form/FormikLazyDropdown';
import { useAppSelector } from '@/hooks/store';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { useToast } from '@/hooks/useToast';
import { selectCurrentAccount } from '@/store/features/account';
import { selectCurrentUser } from '@/store/features/users';
import { AppButton } from '@/ui/buttons';
import { AppCalendar } from '@/ui/calendar';
import { CheckboxesContainer } from '@/ui/checkboxes-container';
import { AppChip } from '@/ui/chip';
import { AppIconTooltip } from '@/ui/icon-tooltip/AppIconTooltip';
import { MediaUpload } from '@/ui/media-upload';
import { Divider, FlexContainer, FormContainer } from '@/ui/styled-ui';
import {
  courseDeliveryLmsOptions,
  isDemoRestrictedMode,
} from '@/utils/helpers';
import { preventDropdownBrowserTranslate } from '@/utils/helpers/ui.helper';
import { PostHogFeatureFlags } from '@/utils/posthog';
import { withPreviewDeployPath } from '@/utils/url';
import { AxiosError } from 'axios';
import classNames from 'classnames';
import { Field, Form, Formik, useFormikContext } from 'formik';
import { debounce } from 'lodash';
import moment from 'moment';
import momentTimezone from 'moment-timezone';
import { Checkbox, CheckboxChangeEvent } from 'primereact/checkbox';
import { DropdownChangeEvent } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import React, { SyntheticEvent, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PhoneInput, { CountryData } from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import styled from 'styled-components';

const StyledPhoneInput = styled(PhoneInput)<{ buttonProps: { title: string } }>`
  background-color: var(--white-main) !important;
  &.react-tel-input .flag-dropdown .selected-flag {
    background-color: var(--white-main) !important;
    &::after {
      content: ${(props) => `'${props.buttonProps.title}'`};
      text-transform: uppercase;
      color: var(--black-main);
      font-family: var(--font-family);
      font-size: var(--small-font-size);
      line-height: var(--small-line-height);
    }

    &.open::after {
      content: ${(props) => `'${props.buttonProps.title}'`};
    }
  }
`;

const StyledInputIconButton = styled.i`
  cursor: pointer;
  transition: color 0.3s;

  &:hover {
    color: var(--red-main) !important;
  }
`;

const StyledSystemSettingsTitle = styled.h3`
  font-size: var(--medium-font-size);
  font-weight: 700;
  line-height: var(--medium-line-height);
  margin: 0;
`;

const StyledCard = styled.div`
  background-color: var(--beige-main);
  border-radius: var(--semi-medium-border-radius);
  padding: var(--medium-big-padding);
  width: 100%;
`;

const StyledItSupportInputs = styled.div`
  margin-top: var(--default-padding);
`;

const StyledBrandingLabel = styled.label`
  display: block;
  margin-bottom: 7px !important;
`;

// The OSINT card is currently hidden. The code will be used when we enable the feature in the future.
// const StyledOsintCard = styled.div`
//   display: flex;
//   flex-direction: column;
//   align-items: flex-start;
//   width: 100%;
//   border: 1px solid var(--gray-moderate);
//   border-radius: var(--xsmall-border-radius);
//   padding: var(--default-padding);
// `;

// const StyledOsintTitle = styled.div`
//   display: flex;
//   align-items: center;
//   gap: var(--xxxsmall-padding);
//   font-size: var(--small-font-size);
//   font-weight: 600;
//   line-height: var(--small-line-height);
//   color: var(--black-main);
// `;

// const StyledOsintDescription = styled.p`
//   font-size: var(--xsmall-font-size);
//   line-height: var(--xsmall-line-height);
//   color: var(--black-main);
//   margin: var(--xsmall-padding) 0 0;
// `;

type AccountFormProps = {
  isCreate: boolean;
  isSystem: boolean;
  initialValues: AccountFormState;
  state?: LoadingStateType;
  disabled?: boolean;
  locked?: boolean;
  commercialAdminMode?: boolean;
  accountId?: string;
  onSubmit: (data: AccountFormState) => void;
  preselectedLogo?: File | null;
  preselectedGuidelinesFile?: File | null;
  selectedAccount?: Account;
  onDescriptionSaved: () => void;
  onDirtyChange?: (isDirty: boolean) => void;
  submitRef?: React.MutableRefObject<(() => Promise<boolean | void>) | null>;
};

const FormikObserver: React.FC<{
  onDirtyChange?: (isDirty: boolean) => void;
  submitRef?: React.MutableRefObject<(() => Promise<boolean | void>) | null>;
}> = ({ onDirtyChange, submitRef }) => {
  const { dirty, submitForm, validateForm } =
    useFormikContext<AccountFormState>();

  useEffect(() => {
    onDirtyChange?.(dirty);
  }, [dirty]);

  useEffect(() => {
    if (!submitRef) return;
    submitRef.current = async () => {
      const errors = await validateForm();
      if (Object.keys(errors).length > 0) {
        await submitForm();
        return false;
      }
      return submitForm();
    };
    return () => {
      submitRef.current = null;
    };
  }, [submitForm, submitRef, validateForm]);

  return null;
};

export const AccountForm: React.FC<AccountFormProps> = ({
  isCreate,
  initialValues,
  state,
  disabled: disabledProp,
  locked,
  commercialAdminMode,
  onSubmit,
  preselectedLogo,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  preselectedGuidelinesFile,
  selectedAccount,
  onDescriptionSaved,
  onDirtyChange,
  submitRef,
}) => {
  const { t } = useTranslation();
  const toast = useToast();
  const { canUseFeature, accountCanUseFeature } = useFeatureFlag();
  const account = useAppSelector(selectCurrentAccount);
  const user = useAppSelector(selectCurrentUser);
  const isDemoAccount = isDemoRestrictedMode(account, user);
  const disabled = disabledProp || commercialAdminMode;
  const accountHasUsers = (selectedAccount?.totalUsersCount ?? 1) > 0;
  const canEditSubdomain =
    SUBDOMAIN_EDITOR_ROLES.includes(user?.role.code as SystemRoles) ||
    (user?.role.code === SystemRoles.SUPER_ADMIN && !accountHasUsers);
  const showSubdomainRestrictionHint =
    !isCreate &&
    accountHasUsers &&
    !initialValues.subdomain &&
    [SystemRoles.DEVELOPER, SystemRoles.SUPER_ADMIN].includes(
      user?.role.code as SystemRoles,
    );
  const [pendingSubdomainValues, setPendingSubdomainValues] =
    useState<AccountFormState | null>(null);

  const handleSubmit = (data: AccountFormState) => {
    if (
      !isCreate &&
      accountHasUsers &&
      !initialValues.subdomain &&
      data.subdomain
    ) {
      setPendingSubdomainValues(data);

      return;
    }

    onSubmit(data);
  };
  const [showLms, setShowLms] = useState<boolean>(false);
  const [accountsWithSameHubspotId, setAccountsWithSameHubspotId] = useState<
    Account[]
  >([]);
  const [countryCode, setCountryCode] = useState<string>('');

  useEffect(() => {
    setShowLms(initialValues.courseDelivery !== AccountCourseDelivery.CP_APP);
  }, [initialValues]);

  const fetchResellers = async (params: ListingRequest) =>
    await client.resellers.getResellers(params);

  const handleFetchAccounts = async (value: string) => {
    try {
      const request = await client.accounts.getAccounts({
        filters: [nested('hubspot', [equal('id', value)])],
      });
      setAccountsWithSameHubspotId(request?.result ?? []);
    } catch (e) {
      handleAxiosError(e as Error | AxiosError, toast);
    }
  };

  const checkForHubspotIdDuplicates = useMemo(
    () =>
      debounce((value: string) => {
        handleFetchAccounts(value);
      }, 500),
    [],
  );

  const timezoneOptions = useMemo(
    () =>
      momentTimezone.tz.names().map((name) => ({
        label: `${name} (UTC${momentTimezone.tz(name).format('Z')})`,
        value: name,
      })),
    [],
  );

  const handleCopySubDomainUrl = async (e: React.MouseEvent<HTMLElement>) => {
    const copyUrl = account?.platformSettings?.url;
    if (!copyUrl) {
      return;
    }

    e.preventDefault();
    await navigator.clipboard.writeText(withPreviewDeployPath(copyUrl));
    toast?.success('', t('clipboard.copied'));
  };

  return (
    <>
      <SubdomainAssignmentConfirmDialog
        subdomain={pendingSubdomainValues?.subdomain}
        onConfirm={() => {
          const values = pendingSubdomainValues;
          setPendingSubdomainValues(null);

          if (values) {
            onSubmit(values);
          }
        }}
        onCancel={() => setPendingSubdomainValues(null)}
      />

      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={accountSchema(t, isCreate)}
        onSubmit={handleSubmit}
        validateOnMount
        validateOnChange
      >
        {({ values, errors, touched, setFieldValue, setFieldTouched }) => {
          return (
            <FormContainer width={100}>
              <Form className="w-full" autoComplete="off">
                <FormikObserver
                  onDirtyChange={onDirtyChange}
                  submitRef={submitRef}
                />
                {/* Core account information */}
                <FlexContainer
                  gap={28}
                  width="100%"
                  justify="space-between"
                  align="flex-start"
                  wrap="wrap"
                >
                  {/* Left column - Account details */}
                  <div className="flex-1">
                    <div className="field w-full mb-4">
                      <Field
                        id="name"
                        name="name"
                        label={t('generic.name')}
                        className="w-full"
                        component={FormikInput}
                        placeholder={t('account.accountName')}
                        required={!initialValues.name}
                        disabled={disabled || locked}
                      />
                    </div>
                    {isCreate && !disabled && (
                      <div className="field w-full mb-4">
                        <Field
                          id="hubspotId"
                          name="hubspotId"
                          label={t('hubspot.companyId')}
                          className="w-full"
                          component={FormikInput}
                          placeholder={t('hubspot.companyId')}
                          required={!initialValues.hubspotId && !values.type}
                          disabled={disabled || values.type || locked}
                          onInput={(
                            event: SyntheticEvent<HTMLInputElement>,
                          ) => {
                            const value = (event.target as HTMLInputElement)
                              .value;
                            setFieldValue('hubspotId', value);

                            if (value) {
                              checkForHubspotIdDuplicates(value);
                            } else {
                              setAccountsWithSameHubspotId([]);
                            }
                          }}
                        />

                        {!!accountsWithSameHubspotId?.length && (
                          <>
                            <span className="secondary-text">
                              {t('hubspot.companyId.duplicates.warning')}
                            </span>
                            <strong className="secondary-text">
                              {accountsWithSameHubspotId
                                ?.map(({ name }) => name)
                                .join(', ')}
                            </strong>
                          </>
                        )}
                      </div>
                    )}
                    {isCreate && !disabled && (
                      <div className="field w-full mb-4">
                        <Field
                          id="hubspotDomains"
                          name="hubspotDomains"
                          label={t('hubspot.domains')}
                          className="w-full"
                          component={FormikChips}
                          placeholder={t('hubspot.domains')}
                          required={
                            !initialValues.hubspotDomains?.length &&
                            !values.type &&
                            isCreate
                          }
                          disabled={disabled || values.type || !isCreate}
                        />
                      </div>
                    )}
                    <FlexContainer gap={5} className="mb-4">
                      <div className="w-full" style={{ marginTop: '2px ' }}>
                        <Field
                          id="subdomain"
                          name="subdomain"
                          label={t('account.subdomain')}
                          className="w-full"
                          component={FormikIconInput}
                          icon={
                            values.subdomain ? (
                              <StyledInputIconButton
                                className="pi pi-clone"
                                onClick={handleCopySubDomainUrl}
                              ></StyledInputIconButton>
                            ) : null
                          }
                          disabled={
                            initialValues.subdomain ||
                            disabled ||
                            locked ||
                            (!isCreate && !canEditSubdomain)
                          }
                          required={isCreate && !values.type}
                          tooltip={
                            showSubdomainRestrictionHint
                              ? t('account.subdomain.restricted')
                              : undefined
                          }
                          labelChip={
                            showSubdomainRestrictionHint &&
                            !canEditSubdomain && (
                              <AppChip
                                label={t('generic.disabled')}
                                type="error"
                                size="xs-auto"
                                className="ml-2"
                              />
                            )
                          }
                        />
                      </div>
                    </FlexContainer>
                    <div className="field w-full mb-4">
                      <Field
                        id="defaultLanguage"
                        label={t('account.defaultLanguage')}
                        name="defaultLanguage"
                        filter
                        onChange={(e: DropdownChangeEvent) =>
                          setFieldValue('defaultLanguage', e.value)
                        }
                        onBlur={() => setFieldTouched('defaultLanguage', true)}
                        className="w-full"
                        component={FormikDropdown}
                        placeholder={t('generic.select')}
                        options={availableLanguagesDropdown.filter(
                          ({ value }) => !courseOnlyLanguages.includes(value),
                        )}
                        disabled={locked}
                        {...preventDropdownBrowserTranslate}
                      />
                    </div>
                    <div className="field w-full mb-4">
                      <Field
                        id="timezone"
                        label={t('account.settings.timezone')}
                        name="timezone"
                        filter
                        onChange={(e: DropdownChangeEvent) =>
                          setFieldValue('timezone', e.value ?? null)
                        }
                        onBlur={() => setFieldTouched('timezone', true)}
                        className="w-full"
                        component={FormikDropdown}
                        placeholder={t('generic.select')}
                        options={timezoneOptions}
                        disabled={locked}
                        tooltip={t('account.settings.timezone.description')}
                        {...preventDropdownBrowserTranslate}
                      />
                    </div>
                    {selectedAccount && (
                      <div className="field w-full mb-4">
                        <CompanyDescriptionSection
                          account={selectedAccount}
                          onDescriptionSaved={onDescriptionSaved}
                        />
                      </div>
                    )}
                    {!isCreate && selectedAccount?.id && (
                      <div className="field w-full mb-4">
                        <OsintTransparencySection
                          accountId={selectedAccount.id}
                          account={selectedAccount}
                        />
                      </div>
                    )}
                  </div>

                  {/* Right column - Branding & IT support */}
                  <div className="flex-1">
                    <div className="field w-full mb-4">
                      <StyledBrandingLabel>
                        {t('account.branding')}
                        <span className="p-error">*</span>
                      </StyledBrandingLabel>
                      <StyledCard>
                        <MediaUpload
                          label={t('account.logo')}
                          description={t('account.upload.modal.text')}
                          purpose={FilePurpose.THUMBNAIL}
                          accept=".png,.jpg,.jpeg,.svg"
                          maxFileSize={2_000_000}
                          displayMode="image"
                          onFileUpload={(file: File | null) =>
                            setFieldValue('companyLogo', file?.id)
                          }
                          preselectedFile={preselectedLogo}
                          isDisabled={locked}
                          noFileText={t('account.no.logo')}
                          uploadButtonText={t('account.upload.logo')}
                        />
                      </StyledCard>
                    </div>
                    <StyledCard>
                      <div className="field-checkbox it-repsonsible-checkbox mb-0">
                        <Field
                          name="showItSupport"
                          label={t('account.add.supportContact')}
                          component={FormikCheckbox}
                          disabled={locked}
                          tooltipId="it-support-tooltip"
                          tooltip={t('account.it.support.tooltip')}
                          onChange={(e: CheckboxChangeEvent) => {
                            setFieldValue('showItSupport', e.checked);
                            if (!e.checked) {
                              setFieldValue('itSupportSectionTitle', '');
                              setFieldValue('itSupportName', '');
                              setFieldValue('itSupportTitle', '');
                              setFieldValue('itSupportEmail', '');
                              setFieldValue('itSupportPhone', '');
                            }
                          }}
                        />
                      </div>
                      {values.showItSupport && (
                        <StyledItSupportInputs>
                          <div className="field w-full mb-4">
                            <Field
                              id="itSupportSectionTitle"
                              name="itSupportSectionTitle"
                              label={t('account.supportContact.type')}
                              className="w-full white"
                              component={FormikInput}
                              placeholder={t('account.it.supportContact')}
                              disabled={locked}
                              required
                            />
                          </div>
                          <FlexContainer gap={14}>
                            <div className="field w-full mb-4 flex-1">
                              <Field
                                id="itSupportName"
                                name="itSupportName"
                                label={t('generic.fullName')}
                                className="w-full white"
                                component={FormikInput}
                                placeholder={t('generic.fullName')}
                                disabled={locked}
                                required
                              />
                            </div>
                            <div className="field w-full mb-4 flex-1">
                              <Field
                                id="itSupportTitle"
                                name="itSupportTitle"
                                label={t('generic.jobTitle')}
                                className="w-full white"
                                component={FormikInput}
                                placeholder={t('generic.jobTitle')}
                                disabled={locked}
                              />
                            </div>
                          </FlexContainer>
                          <div className="field w-full mb-4">
                            <Field
                              id="itSupportEmail"
                              label={t('generic.email')}
                              name="itSupportEmail"
                              className="w-full white"
                              onChange={(
                                e: React.ChangeEvent<HTMLInputElement>,
                              ) =>
                                setFieldValue(
                                  'itSupportEmail',
                                  e.target.value.replace(/ /g, ''),
                                )
                              }
                              component={FormikInput}
                              placeholder={t('generic.workEmail')}
                              disabled={locked}
                              required
                            />
                          </div>
                          <div className="field w-full mb-0">
                            <label
                              htmlFor="itSupportPhone"
                              className={classNames({
                                'p-error':
                                  touched.itSupportPhone &&
                                  !!errors.itSupportPhone,
                              })}
                            >
                              {t('generic.phone')}
                              <span className="p-error">*</span>
                            </label>
                            <StyledPhoneInput
                              country={'dk'}
                              enableTerritories={true}
                              priority={{ gb: 0, je: 1 }}
                              value={values.itSupportPhone}
                              onMount={(value: string, data: CountryData) => {
                                if (data?.countryCode) {
                                  setCountryCode(data.countryCode);
                                }
                              }}
                              onChange={(value: string, data: CountryData) => {
                                setFieldValue('itSupportPhone', value);
                                if (data?.countryCode) {
                                  setCountryCode(data.countryCode);
                                }
                              }}
                              enableSearch={true}
                              prefix="+"
                              enableAreaCodes={true}
                              disabled={locked}
                              disableDropdown={false}
                              searchPlaceholder={t('generic.search')}
                              buttonProps={{
                                title: countryCode || '',
                              }}
                              inputProps={{
                                name: 'itSupportPhone',
                                autoFocus: false,
                              }}
                              placeholder={t('generic.phoneNumber')}
                            />
                            {touched.itSupportPhone &&
                            !!errors.itSupportPhone ? (
                              <small className="p-error">
                                {errors.itSupportPhone}
                              </small>
                            ) : null}
                          </div>
                        </StyledItSupportInputs>
                      )}
                    </StyledCard>
                  </div>
                </FlexContainer>

                {/* System settings section */}
                {!disabledProp && (
                  <>
                    <Divider className="mt-5 mb-5" />
                    <StyledSystemSettingsTitle className="mb-4">
                      {t('account.systemSettings.title')}
                    </StyledSystemSettingsTitle>
                    <FlexContainer
                      gap={28}
                      width="100%"
                      justify="space-between"
                      align="flex-start"
                      wrap="wrap"
                    >
                      {/* Left column - System fields */}
                      <div className="flex-1">
                        {!values?.mainAccountOfReseller && (
                          <div className="field w-full mb-4">
                            <Field
                              id="reseller"
                              label={t('reseller')}
                              name="reseller"
                              showClear
                              filter
                              onChange={(e: DropdownChangeEvent) =>
                                setFieldValue('reseller', e.value)
                              }
                              onBlur={() => setFieldTouched('reseller', true)}
                              optionLabel="name"
                              className="w-full"
                              component={FormikLazyDropdown}
                              placeholder={t('generic.select')}
                              fetchService={fetchResellers}
                              disabled={disabledProp || locked}
                            />
                          </div>
                        )}
                        {!!values?.mainAccountOfReseller && (
                          <div className="field w-full mb-4">
                            <label htmlFor="mainAccountOfReseller">
                              {t('reseller.mainAccount.forReseller')}
                            </label>
                            <InputText
                              value={
                                values.mainAccountOfReseller?.name ??
                                values.mainAccountOfReseller?.id
                              }
                              id="mainAccountOfReseller"
                              name="mainAccountOfReseller"
                              className="w-full"
                              disabled={true}
                            />
                          </div>
                        )}
                        <div className="field w-full mb-4">
                          <Field
                            id="active"
                            name="active"
                            label={t('generic.status')}
                            className="w-full"
                            component={FormikDropdown}
                            placeholder={t('generic.select')}
                            options={[
                              {
                                label: t('generic.active'),
                                value: true,
                              },
                              {
                                label: t('generic.inactive'),
                                value: false,
                              },
                            ]}
                            disabled={disabled || locked}
                          />
                        </div>
                        <div className="field w-full mb-4">
                          <Field
                            id="mailSystem"
                            name="mailSystem"
                            label={t('account.mailSystem')}
                            className="w-full"
                            component={FormikDropdown}
                            placeholder={t('generic.select')}
                            options={[
                              {
                                label: t('account.mailSystem.microsoft'),
                                value: MailSystemType.MICROSOFT_365,
                              },
                              {
                                label: t('account.mailSystem.google'),
                                value: MailSystemType.GOOGLE_WORKSPACE,
                              },
                            ]}
                            disabled={disabledProp || locked}
                            showClear
                          />
                          <small className="secondary-text">
                            {t('account.mailSystem.helperText')}
                          </small>
                        </div>
                        {(user?.role.code === SystemRoles.DEVELOPER ||
                          user?.role.code === SystemRoles.SUPER_ADMIN) && (
                          <div className="field w-full mb-4">
                            <Field
                              id="sendingProfileType"
                              name="sendingProfileType"
                              label={t('account.sendingProfileType')}
                              className="w-full"
                              component={FormikDropdown}
                              placeholder={t('generic.select')}
                              options={[
                                {
                                  label: t('dialog.yes'),
                                  value: SendingProfileType.CONNECTOR,
                                },
                                {
                                  label: t('dialog.no'),
                                  value: SendingProfileType.HEYSENDER,
                                },
                              ]}
                              disabled={disabled || locked}
                            />
                          </div>
                        )}
                        {ACCOUNT_FIELD_EDITOR_ROLES.includes(
                          user?.role.code as SystemRoles,
                        ) && (
                          <div className="field-checkbox">
                            <Field
                              inputId="isPhishingSenderWhitelisted"
                              name="isPhishingSenderWhitelisted"
                              label={t('account.isPhishingSenderWhitelisted')}
                              component={FormikCheckbox}
                              disabled={locked}
                            />
                          </div>
                        )}
                        {(user?.role.code === SystemRoles.DEVELOPER ||
                          user?.role.code === SystemRoles.SUPER_ADMIN) &&
                          !values.freeTrialEndsAt && (
                            <div className="field-checkbox">
                              <Field
                                inputId="enableUserflow"
                                name="enableUserflow"
                                label={t('account.enableUserflow')}
                                component={FormikCheckbox}
                                disabled={disabled || locked}
                              />
                            </div>
                          )}
                        <div className="field w-full mb-4">
                          <label htmlFor="awarenessGoLiveDate">
                            {t('account.awarenessGoLiveDate')}
                          </label>
                          <AppCalendar
                            id="awarenessGoLiveDate"
                            name="awarenessGoLiveDate"
                            className="w-full"
                            showIcon
                            iconPos="left"
                            placeholder={t(
                              'account.select.awarenessGoLiveDate',
                            )}
                            value={
                              values.awarenessGoLiveDate
                                ? moment(values.awarenessGoLiveDate?.toString())
                                    .utc(true)
                                    .toDate()
                                : null
                            }
                            onChange={(event) =>
                              setFieldValue(
                                'awarenessGoLiveDate',
                                event?.value
                                  ? moment(event?.value?.toString())
                                      .utc(true)
                                      .toDate()
                                  : null,
                              )
                            }
                            showButtonBar
                            readOnlyInput
                            disabled={disabled || locked}
                          />
                        </div>
                      </div>

                      {/* Right column - Free trial settings + Type and features */}
                      <div className="flex-1 flex flex-column gap-4">
                        <CheckboxesContainer
                          label={t('account.freeTrial.settings')}
                        >
                          <div
                            className="field-checkbox mb-0"
                            data-id="isFreeTrial"
                          >
                            <Field
                              inputId="isFreeTrial"
                              name="isFreeTrial"
                              label={t('account.set.freeTrial')}
                              component={FormikCheckbox}
                              disabled={disabledProp || locked}
                              onChange={(e: CheckboxChangeEvent) => {
                                setFieldValue('isFreeTrial', e.checked);
                                if (!e.checked) {
                                  setFieldValue('enableUserflow', false);
                                }
                              }}
                            />
                          </div>
                          {values.isFreeTrial && user?.account.isSystem && (
                            <div
                              className="flex flex-column"
                              style={{ gap: 'var(--small-padding)' }}
                            >
                              <div className="field w-full mb-0">
                                <label
                                  htmlFor="free-trial-ends-at"
                                  id="free-trial-ends-at-label"
                                >
                                  {t('account.freeTrial.endDate')}
                                </label>
                                <AppCalendar
                                  id="free-trial-ends-at"
                                  name="freeTrialEndsAt"
                                  className="w-full white-input"
                                  showIcon
                                  iconPos="left"
                                  placeholder={t(
                                    'account.select.freeTrial.endDate',
                                  )}
                                  value={
                                    values.freeTrialEndsAt
                                      ? moment(
                                          values.freeTrialEndsAt?.toString(),
                                        )
                                          .utc(true)
                                          .toDate()
                                      : null
                                  }
                                  minDate={new Date()}
                                  onChange={(event) =>
                                    setFieldValue(
                                      'freeTrialEndsAt',
                                      moment(event?.value?.toString())
                                        .utc(true)
                                        .toDate(),
                                    )
                                  }
                                  readOnlyInput
                                />
                              </div>
                              {values.freeTrialDeletionDate && (
                                <div className="field w-full mb-0">
                                  <label htmlFor="free-trial-deletion-date">
                                    {t('account.freeTrial.deletionDate')}
                                  </label>
                                  <AppCalendar
                                    id="free-trial-deletion-date"
                                    name="freeTrialDeletionDate"
                                    className="w-full white-input"
                                    value={
                                      values.freeTrialDeletionDate
                                        ? moment(
                                            values.freeTrialDeletionDate?.toString(),
                                          )
                                            .utc(true)
                                            .toDate()
                                        : null
                                    }
                                    readOnlyInput
                                    disabled
                                  />
                                </div>
                              )}
                              {(canUseFeature(
                                PostHogFeatureFlags.FREE_TRIAL_PLATFORM_RESTRICTIONS,
                              ) ||
                                accountCanUseFeature(
                                  PostHogFeatureFlags.FREE_TRIAL_PLATFORM_RESTRICTIONS,
                                  selectedAccount?.featureFlagAbility,
                                )) && (
                                <div
                                  className="field-checkbox mb-0"
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                  }}
                                >
                                  <Field
                                    inputId="freeTrialFullAccess"
                                    name="freeTrialFullAccess"
                                    label={t(
                                      'account.freeTrial.fullAccess.label',
                                    )}
                                    component={FormikCheckbox}
                                    disabled={disabledProp || locked}
                                  />
                                  <AppIconTooltip
                                    target="free-trial-full-access-tooltip"
                                    content={t(
                                      'account.freeTrial.fullAccess.tooltip',
                                    )}
                                    position="bottom"
                                  />
                                </div>
                              )}
                            </div>
                          )}
                        </CheckboxesContainer>
                        <CheckboxesContainer
                          label={t('account.typeAndFeatures')}
                        >
                          <div className="field-checkbox mb-0" data-id="type">
                            <Field
                              inputId="type"
                              name="type"
                              label={t('account.mark.test')}
                              component={FormikCheckbox}
                              disabled={disabled || locked}
                            />
                          </div>
                          <div
                            className="field-checkbox mb-0"
                            data-id="showLms"
                          >
                            <Checkbox
                              inputId="showLms"
                              checked={showLms}
                              name="showLms"
                              onChange={(e) => {
                                setShowLms(!!e.checked);
                                if (!e.checked) {
                                  setFieldValue(
                                    'courseDelivery',
                                    AccountCourseDelivery.CP_APP,
                                  );
                                }
                              }}
                              disabled={locked}
                            />
                            <label htmlFor="courseDelivery">
                              {t('account.set.courseDelivery')}
                            </label>
                          </div>
                          {showLms && (
                            <div className="field w-8 mb-0">
                              <Field
                                id="courseDelivery"
                                label={t('account.courseDelivery')}
                                name="courseDelivery"
                                filter
                                onBlur={() =>
                                  setFieldTouched('courseDelivery', true)
                                }
                                className="w-full white"
                                component={FormikDropdown}
                                placeholder={t('generic.select')}
                                options={courseDeliveryLmsOptions(t)}
                                disabled={locked}
                              />
                            </div>
                          )}
                          <div
                            className="field-checkbox mb-0"
                            data-id="movedToScannet"
                          >
                            <Field
                              inputId="movedToScannet"
                              name="movedToScannet"
                              label={t('account.movedToScannet')}
                              component={FormikCheckbox}
                              disabled={disabled || locked}
                            />
                          </div>
                        </CheckboxesContainer>
                        {user?.account?.isSystem && (
                          <div className="field w-full mb-4">
                            <Field
                              id="linkedInDescription"
                              name="linkedInDescription"
                              label={t('account.linkedInDescription')}
                              className="w-full"
                              component={FormikTextarea}
                              placeholder={t(
                                'account.linkedInDescription.placeholder',
                              )}
                              rows={4}
                              autoResize
                              disabled={disabled || locked}
                            />
                          </div>
                        )}
                      </div>
                    </FlexContainer>
                  </>
                )}

                <FlexContainer
                  direction="column"
                  align="flex-start"
                  className="mt-4 mb-5"
                >
                  {!locked && !submitRef && (
                    <AppButton
                      isSubmit={!isDemoAccount}
                      severity="success"
                      label={
                        initialValues.name
                          ? t('button.saveSettings')
                          : t('button.create')
                      }
                      state={state}
                      isDisabled={!!Object.keys(errors).length}
                    />
                  )}
                </FlexContainer>
              </Form>
            </FormContainer>
          );
        }}
      </Formik>
    </>
  );
};
